"use client";

import { Toggle } from "@/components/form/Toggle";
import { ImportantText } from "@/components/ImportantText";
import { Badge } from "@/components/text/Badge";
import { Heading4 } from "@/components/text/Heading4";
import { SoftText } from "@/components/text/SoftText";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { IsolationState } from "@/types/defs/audioQuality/isolationBadges";

export function PrepareAudioSettings({
  isCoverSelected,
  isInstrumentSelected,
  shouldShowLoadState,
  instrumentalRemoval,
  reverbRemoval,
  removeInstrumental,
  deReverbDeEcho,
  shouldShowAutoSettingsBadge,
}: {
  isCoverSelected: boolean;
  isInstrumentSelected: boolean;
  shouldShowLoadState: boolean;
  instrumentalRemoval: IsolationState;
  reverbRemoval: IsolationState;
  removeInstrumental: boolean;
  deReverbDeEcho: boolean;
  shouldShowAutoSettingsBadge: boolean;
}) {
  const setRemoveInstrumental = useConversionHyperParameters(
    (state) => state.setRemoveInstrumental
  );
  const removeBackgroundVocals = useConversionHyperParameters(
    (state) => state.removeBackgroundVocals
  );
  const setRemoveBackgroundVocals = useConversionHyperParameters(
    (state) => state.setRemoveBackgroundVocals
  );
  const setDeReverbDeEcho = useConversionHyperParameters(
    (state) => state.setDeReverbDeEcho
  );
  const deNoise = useConversionHyperParameters((state) => state.deNoise);
  const setDeNoise = useConversionHyperParameters((state) => state.setDeNoise);

  return (
    <div className="mt-6 border-t border-gray-900/5 pt-6">
      <div className="flex items-center justify-between">
        <div className="mb-4">
          <Heading4 elementType="h3" className="mr-2">
            Prepare audio for conversion
          </Heading4>
          <SoftText
            elementType="p"
            className="mr-2 mt-1 text-sm font-normal text-gray-500"
          >
            This tool requires a dry monophonic vocal recording.
          </SoftText>
        </div>
        {shouldShowAutoSettingsBadge && (
          <Badge
            dotColor="green"
            className="text-nowrap whitespace-nowrap bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
          >
            Auto settings applied
          </Badge>
        )}
      </div>

      {isCoverSelected && (
        <ImportantText className="mb-4">
          If the track is not completely dry, make use of the isolation
          settings.
        </ImportantText>
      )}

      <Toggle
        toggled={removeInstrumental}
        setEnabled={setRemoveInstrumental}
        label="Remove instrumental"
        testId="remove-instrumental-toggle"
        disabled={shouldShowLoadState}
        badge={
          !isInstrumentSelected &&
          instrumentalRemoval !== "off" && (
            <Badge
              dotColor={removeInstrumental ? "green" : "yellow"}
              className="h-fit bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
            >
              Recommended
            </Badge>
          )
        }
      >
        Enable if your audio has an instrumental.
      </Toggle>
      <Toggle
        toggled={removeBackgroundVocals}
        setEnabled={setRemoveBackgroundVocals}
        label="Remove background vocals"
        testId="remove-background-vocals-toggle"
        className="mt-4"
        disabled={shouldShowLoadState}
      >
        Enable if your audio has background vocals.
      </Toggle>
      <Toggle
        toggled={deReverbDeEcho}
        setEnabled={setDeReverbDeEcho}
        label="Remove reverb and echo"
        testId="remove-reverb-and-echo-toggle"
        className="mt-4"
        disabled={shouldShowLoadState}
        badge={
          !isInstrumentSelected &&
          reverbRemoval !== "off" && (
            <Badge
              dotColor={deReverbDeEcho ? "green" : "yellow"}
              className="h-fit bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
            >
              Recommended
            </Badge>
          )
        }
      >
        Enable if your audio has reverb or echo.
      </Toggle>
      <Toggle
        toggled={deNoise}
        setEnabled={setDeNoise}
        label="Remove noise"
        testId="remove-noise-toggle"
        className="mt-4"
        disabled={shouldShowLoadState}
      >
        Enable if your audio has background noise.
      </Toggle>
    </div>
  );
}
